import axios from 'axios';
import { getBackendHeaders, fetchBackendChallenge, authenticatedApi } from './backend-auth';
import { uploadManager } from 'helpers/UploadContext';
import { removeVideo, saveVideo } from 'helpers/UploadDB';
import { clearUploadState, saveUploadState } from 'helpers/UploadState';
import { v4 as uuidv4 } from "uuid";
export let backend = (process.env.DEV_MODE == "true") ? process.env.SPEEM_BACKEND_DEV : process.env.SPEEM_BACKEND;
export const aiBackend = backend.replace(/\/$/, '') + '/api/ai';


const activeUploads: Record<string, AbortController> = {};
export async function get_db_notifications(all = false) {
    let method = all ? `notifications/all` : `notifications`;
    let res = await (all ? authenticatedApi : axios).get(`${backend}/api/${method}`);
    return res.data;
}
export async function get_carousel_items() {
    let res = await axios.get(`${backend}/api/carousel`);
    return res.data;
}

export const backend_get_challenge = fetchBackendChallenge;

export async function backend_login(username: string, signature: string, challenge: string) {
    try {
        let res = await axios.post(`${backend}/api/auth/login`, { username, signature, challenge });
        return res.data;
    } catch (error) {
        console.warn("Backend login failed.");
        return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
    }
}

export async function backend_refresh_token(token: string) {
    try {
        let res = await axios.post(`${backend}/api/auth/refresh`, { token });
        return res.data;
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || error.message };
    }
}
export async function claim_leaderboard_reward(username, week_start) {
    try {
        let res = await authenticatedApi.post(`${backend}/api/leaderboard/claim`, { username, week_start });
        return res.data;
    } catch (error) {
        console.error("Claim error:", error);
        return { success: false, error: error.response?.data?.error || error.message };
    }
}

export async function get_ipfs_usage(username = "") {
    try {
        let params = username ? { username } : {};
        let res = await axios.post(`${backend}/api/ipfs/files`, params, { headers: await getBackendHeaders() });
        return res.data;
    } catch (error) {
        console.log("While getting files", username);
        return [];
    }

}
export const register_view = (author, permlink, recently_viewed, updateRecentlyViewed, user = null) => {
    try {
        const token = localStorage.getItem('jwt_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const postUrl = `/@${author}/${permlink}`;
        const today = new Date().toISOString().slice(0, 10);
        let viewedList = [];
        let alreadyViewed = false;
        if (recently_viewed) {
            viewedList = recently_viewed ? recently_viewed : false;
            if (viewedList) {
                alreadyViewed = viewedList.find(
                    (view) => view.url === postUrl && view.date === today
                );
            }
        }
        console.log("Already viewed?", alreadyViewed);

        axios
            .post(`${backend}/api/content_views/increment`, {
                url: postUrl,
                username: user,
                alreadyViewed: alreadyViewed
            }, { headers })
            .then(() => {
                if (!alreadyViewed) {
                    const newView = { url: postUrl, date: today };
                    const cleanedList = viewedList.filter((view) => view.date === today);
                    const updatedList = [...cleanedList, newView];
                    updateRecentlyViewed(updatedList);
                } else {
                    console.log("Already viewed today:", postUrl);
                }
            })
            .catch((error) => {
                console.error("Error incrementing views:", error);
            });

    } catch (error) {
        console.log(error);

    }

};
export async function upload_video(file, user, onUploadProgress) {
    const uploadId = uuidv4();
    const initialized = await axios.post(`${backend}/api/upload/init`, {
        uploadId, filename: file.name, size: file.size,
        mimeType: file.type || 'video/mp4', mode: 'direct'
    }, { headers: await getBackendHeaders(user) });
    if (initialized.data.result) return { data: initialized.data.result };
    const formData = new FormData();
    formData.append("video", file);
    return axios.post(`${backend}/api/upload?uploadId=${encodeURIComponent(uploadId)}`, formData, {
        headers: { "Content-Type": "multipart/form-data", ...await getBackendHeaders(user) },
        onUploadProgress
    });
}

export async function cancelUpload(uploadId: string) {
    const controller = activeUploads[uploadId];
    if (controller) {
        controller.abort();
        delete activeUploads[uploadId];
    }
    try {
        await axios.post(`${backend}/api/upload/cancel`, { uploadId },
            { headers: await getBackendHeaders() });
    } catch (error: any) {
        if (![404, 410].includes(error.response?.status)) {
            console.warn("Upload cancellation was not confirmed; local resume data was retained.");
            return false;
        }
    }
    clearUploadState();
    await removeVideo(uploadId);
    uploadManager.remove(uploadId);
    return true;
}
export async function upload_video_chunked(
    file,
    user,
    setProgress,
    setPaused = (t) => { t; },
    setPausedReason = (t) => { t; },
    setLoaded = (t) => { t; },
    existingUploadId = null,
    existingUploadedChunks = 0
) {
    const CHUNK_SIZE = 1 * 1024 * 1024;
    const uploadId = existingUploadId || uuidv4();
    const controller = new AbortController();
    activeUploads[uploadId] = controller;

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadedChunks = existingUploadedChunks;
    let paused = false;
    paused;
    let uploading = false;
    let canceled = false;
    let resolveUpload: (result: any) => void;
    let rejectUpload: (reason: any) => void;

    const uploadPromise = new Promise<any>((resolve, reject) => {
        resolveUpload = resolve;
        rejectUpload = reject;
    });

    // guardar archivo si es nuevo upload
    if (!existingUploadId && !canceled) {
        await saveVideo(uploadId, file);
        if (controller.signal.aborted) return;
        saveUploadState({
            uploadId,
            filename: file.name,
            size: file.size,
            uploadedChunks: 0
        });

    }


    try {
        const initialized = await axios.post(`${backend}/api/upload/init`, {
            uploadId, filename: file.name || 'upload.mp4', size: file.size,
            mimeType: file.type || 'video/mp4', totalChunks, mode: 'chunked'
        }, { headers: await getBackendHeaders(user) });
        if (initialized.data.result) {
            const data = initialized.data.result;
            await removeVideo(uploadId);
            uploadManager.remove(uploadId);
            saveUploadState({ uploadId, filename: file.name, size: file.size,
                uploadedChunks: totalChunks, cid: data.cid, url: data.url });
            delete activeUploads[uploadId];
            return { data };
        }
        if (initialized.data.state === 'processing') {
            setPaused(true);
            setPausedReason("The server is processing this upload. Retry its status shortly.");
            delete activeUploads[uploadId];
            return null;
        }
        const received = new Set<number>((initialized.data.receivedChunks || [])
            .filter((index: number) => Number.isInteger(index) && index >= 0 && index < totalChunks));
        uploadedChunks = 0;
        while (received.has(uploadedChunks)) uploadedChunks++;
    } catch (error: any) {
        setPaused(true);
        setPausedReason(error.response?.data?.message || error.message || "Unable to initialize upload.");
        delete activeUploads[uploadId];
        throw error;
    }
    uploadManager.start(uploadId, { file, user });

    // evitar múltiples listeners
    if (!window.__UPLOAD_EVENTS__) {
        window.__UPLOAD_EVENTS__ = true;
        window.addEventListener("offline", () => {
            console.log("Network offline");
        });
        window.addEventListener("online", () => {
            console.log("Network online");
        });
    }

    let activeChunksLoadedBytes: Record<number, number> = {};

    async function uploadChunk(i: number) {

        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append("chunk", chunk);
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", i.toString());
        formData.append("totalChunks", totalChunks.toString());

        let uploaded = false;
        let timeout = 30000;
        while (!uploaded && !canceled) {
            if (controller.signal.aborted) {
                console.log("Upload canceled globally");
                canceled = true;
                return;
            }
            const requestController = new AbortController();
            const onAbort = () => requestController.abort();
            controller.signal.addEventListener("abort", onAbort);
            if (!navigator.onLine) {
                paused = true;
                setPaused(true);
                setPausedReason("⚠️ Waiting for network...");
                await new Promise(r => setTimeout(r, 3000));
                continue;
            }

            if (paused) {
                paused = false;
                setPaused(false);
                setPausedReason("");
            }
            let stalledTimer;
            try {
                activeChunksLoadedBytes[i] = 0;
                let lastLoaded = 0;

                await axios.post(`${backend}/api/upload/chunk?uploadId=${encodeURIComponent(uploadId)}&chunkIndex=${i}`, formData, {
                    signal: requestController.signal,
                    timeout: timeout,
                    headers: { "Content-Type": "multipart/form-data", ...await getBackendHeaders(user) },
                    onUploadProgress: (e) => {
                        const loaded = e.loaded || 0;

                        clearTimeout(stalledTimer);
                        stalledTimer = setTimeout(() => {
                            if (loaded <= lastLoaded) {
                                console.log("Upload stalled — retrying chunk");
                                requestController.abort();
                            }
                        }, 15000);

                        lastLoaded = loaded;
                        activeChunksLoadedBytes[i] = loaded;

                        let sumActiveLoaded = 0;
                        for (let key in activeChunksLoadedBytes) {
                            sumActiveLoaded += activeChunksLoadedBytes[key];
                        }

                        const totalLoadedBytes = uploadedChunks * CHUNK_SIZE + sumActiveLoaded;
                        setLoaded(totalLoadedBytes);

                        const totalProgress = Math.min(
                            100,
                            Math.round((totalLoadedBytes / file.size) * 100)
                        );

                        setProgress(totalProgress);
                        uploadManager.emit(uploadId, {
                            progress: totalProgress,
                            loaded: totalLoadedBytes
                        });
                    }
                });
                controller.signal.removeEventListener("abort", onAbort);
                clearTimeout(stalledTimer);
                uploaded = true;
            } catch (err: any) {
                controller.signal.removeEventListener("abort", onAbort);
                clearTimeout(stalledTimer);
                if (controller.signal.aborted || err.code === 'AUTH_REQUIRED'
                    || (err.response?.status >= 400 && err.response?.status < 500)) {
                    throw err;
                }
                if (err.code === "ECONNABORTED") {
                    console.log("Chunk timeout — waiting for network...");

                    paused = true;
                    setPaused(true);
                    setPausedReason("⚠️ Network slow — retrying...");
                    timeout = Math.min(timeout + 10000, 120000);

                    await new Promise(r => setTimeout(r, 5000));
                    continue;
                }
                if (err.name === "CanceledError") {
                    console.log("Retrying chunk after cancel...");
                } else {
                    console.log("Retrying chunk due to network error...");
                }

                await new Promise(r => setTimeout(r, 2000));
            }
        }
    }

    async function resumeUpload() {
        if (uploading) return;
        uploading = true;
        paused = false;
        setPaused(false);
        setPausedReason("");
        activeChunksLoadedBytes = {};
        const CONCURRENCY = 3;

        for (let i = uploadedChunks; i < totalChunks; i += CONCURRENCY) {
            if (canceled || controller.signal.aborted) {
                console.log("Upload stopped before chunk batch");
                resolveUpload(null);
                return;
            }

            let batch = [];
            let batchSize = 0;
            for (let j = 0; j < CONCURRENCY && (i + j) < totalChunks; j++) {
                batch.push(uploadChunk(i + j));
                batchSize++;
            }

            await Promise.all(batch);

            if (canceled || controller.signal.aborted) {
                console.log("Upload stopped after chunk batch");
                resolveUpload(null);
                return;
            }

            uploadedChunks += batchSize;
            for (let j = 0; j < CONCURRENCY && (i + j) < totalChunks; j++) {
                delete activeChunksLoadedBytes[i + j];
            }

            saveUploadState({
                uploadId,
                filename: file.name,
                size: file.size,
                uploadedChunks
            });
        }
        try {
            if (!canceled) {
                const res = await axios.post(`${backend}/api/upload/complete`, {
                    uploadId
                }, { headers: await getBackendHeaders(user) });

                await removeVideo(uploadId);
                uploadManager.remove(uploadId);
                saveUploadState({
                    uploadId,
                    filename: file.name,
                    size: file.size,
                    uploadedChunks,
                    cid: res.data.cid,
                    url: res.data.url
                });
                resolveUpload(res);
            } else {
                paused = true;
                setPaused(true);
                setPausedReason("⚠️ Cancelled.");
            }

        } catch (err: any) {
            setPaused(true);
            setPausedReason(err.response?.data?.message || err.message || "Unable to complete upload.");
            rejectUpload(err);
        }
    }

    resumeUpload().catch((error: any) => {
        canceled = true;
        controller.abort();
        setPaused(true);
        setPausedReason(error.response?.data?.message || error.message || "Upload stopped.");
        rejectUpload(error);
    });

    try {
        return await uploadPromise;
    } finally {
        delete activeUploads[uploadId];
    }

}
export async function create_banner(p) {
    let params: any = p;
    let res = await authenticatedApi.post(`${backend}/api/carousel`, params);
    return res.data;
}
export async function delete_banner(id) {
    let params: any = { id };
    let res = await authenticatedApi.post(`${backend}/api/carousel/delete`, params);
    return res.data;
}
export async function update_notification(id, status) {
    let params: any = { status, id };
    let res = await authenticatedApi.post(`${backend}/api/notifications/update`, params);
    return res.data;
}
export async function create_notification(p) {
    let params: any = p;
    let res = await authenticatedApi.post(`${backend}/api/notifications/create`, params);
    return res.data;
}
export async function delete_notification(id) {
    let params: any = { id };
    let res = await authenticatedApi.post(`${backend}/api/notifications/delete`, params);
    return res.data;
}
export async function delete_ipfs_content(cid, username) {
    let params: any = { cid, username };
    let res = await axios.post(`${backend}/api/ipfs/delete`, params, { headers: await getBackendHeaders() });
    return res.data;
}
export async function get_views(username: string = "") {
    let params: any = (username) ? { username } : {};
    let res = await axios.post(`${backend}/api/content_views`, params);
    return res.data;
}
export async function get_plans(username: string = "") {
    let params: any = (username) ? { username } : {};
    let res = await axios.post(`${backend}/api/user_plans`, params);
    return res.data;
}
export async function get_user_directories(username: string = "") {
    let params: any = (username) ? { username } : {};
    let res = await authenticatedApi.post(`${backend}/api/user_directories`, params);
    return res.data;
}
export async function create_user_directory(p) {
    let params: any = p;
    let res = await authenticatedApi.post(`${backend}/api/user_directories/create`, params);
    return res.data;
}
export async function get_user_templates(username: string = "") {
    let params: any = (username) ? { username } : {};
    let res = await authenticatedApi.post(`${backend}/api/user_templates`, params);
    return res.data;
}
export async function create_user_template(p: any) {
    let params: any = p;
    let res = await authenticatedApi.post(`${backend}/api/user_templates/create`, params);
    return res.data;
}
export async function update_user_template(p: any) {
    let params: any = p;
    let res = await authenticatedApi.post(`${backend}/api/user_templates/update`, params);
    return res.data;
}
export async function delete_user_template(id: number) {
    let res = await authenticatedApi.post(`${backend}/api/user_templates/delete`, { id });
    return res.data;
}
export async function get_user_preferences(username: string = "") {
    if (!username) return [];
    let params: any = (username) ? { username } : {};
    let res = await authenticatedApi.post(`${backend}/api/user_preferences`, params);
    return res.data;
}
export async function set_user_preferences(p) {
    let params: any = p;
    let res = await authenticatedApi.post(`${backend}/api/user_preferences/set`, params);
    return res.data;
}
export async function set_posting_preferences(username: string, posting_preferences: any) {
    let res = await authenticatedApi.post(`${backend}/api/user_preferences/set_posting`, { username, posting_preferences: JSON.stringify(posting_preferences) });
    return res.data;
}
export async function remove_auto_tag(username: string, tag: string) {
    const token = localStorage.getItem('jwt_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    let res = await authenticatedApi.post(`${backend}/api/user_preferences/remove_auto_tag`, { username, tag }, { headers });
    return res.data;
}
export async function find_posts(limit = 40, start = 0, q = ""): Promise<any> {
    let res = await axios.post(`${backend}/api/find_posts`, { limit, start, q });
    res = res.data;
    let posts = parse(res);
    return (posts);
}
export async function custom_post_queue(username, limit = 40, start = 0): Promise<any> {
    let res = await (username ? authenticatedApi : axios).post(`${backend}/api/custom_post_queue`, { username, limit, start });

    res = res.data;
    let posts = parse(res);
    return (posts);
}
export function skip_video(username: string, author: string, permlink: string): void {
    if (!username || !author || !permlink) return;
    authenticatedApi
        .post(`${backend}/api/skip_video`, { username, author, permlink })
        .then(() => console.log(`[skip_video] Skipped @${author}/${permlink} for ${username}`))
        .catch((err) => console.warn('[skip_video] Failed silently:', err?.message));
}
export async function get_post(author: string, permlink: string): Promise<any> {
    const res = await axios.post(`${backend}/api/get_post`, { author, permlink });
    if (res.data?.error && res.data?.postType) {
        return res.data;
    }
    let post = parse([res.data]);
    return post;
}

export async function top_posts_latest(limit = 40, start = 0): Promise<any> {
    let res = await axios.post(`${backend}/api/top_posts_latest`, { limit, start })
    // return (res.data); 
    res = res.data;
    let posts = parse(res);
    return (posts);
}
export async function top_posts_alltime(limit = 40, start = 0): Promise<any> {
    let res = await axios.post(`${backend}/api/top_posts_alltime`, { limit, start })
    res = res.data;
    let posts = parse(res);
    return (posts);
}
export async function author_views(username): Promise<any> {
    let res = (await axios.post(`${backend}/api/author_views`, { username }));
    return (res.data[0]);
}
export async function is_admin(username): Promise<any> {
    let res = (await authenticatedApi.post(`${backend}/api/user/permissions`, { username }));
    return (res.data);
}
export async function get_quota(username): Promise<any> {
    let res = (await axios.post(`${backend}/api/user/quota`, { username }, { headers: await getBackendHeaders() }));
    return (res.data);
}
export async function author_posts(username, limit = 100, start = 0, sort = "top"): Promise<any> {
    let res: any = (await axios.post(`${backend}/api/author_posts`, { username, limit, start, sort }));
    res = res.data;
    let posts = parse(res);
    return (posts);
}
function parse(res) {
    let posts = [];
    for (let i = 0; i < res.length; i++) {
        const e = res[i];
        let metadata = e.metadata;
        if (typeof metadata == "string") {
            try {
                metadata = (typeof metadata !== "string" || !metadata || metadata.trim() === "undefined") ? {} : JSON.parse(metadata);
            } catch {
                metadata = {};
            }
        }
        if (!metadata) {
            continue; // Skip items with no metadata instead of returning empty array
        }
        posts.push({
            ...metadata,
            views: e.count
        });
    }

    return (posts);
}

export async function get_signup_captcha() {
    const res = await axios.get(`${backend}/api/signup/captcha`);
    return res.data;
}

export async function create_signup_order(params: { email: string, username: string, metadata: any, captchaId: string, captchaText: string }) {
    const res = await axios.post(`${backend}/api/signup/order`, params);
    return res.data;
}

export async function validate_signup_code(code: string) {
    const res = await axios.post(`${backend}/api/signup/validate`, { code });
    return res.data;
}

export async function resend_signup_code(params: { email: string, username: string, captchaId: string, captchaText: string }) {
    const res = await axios.post(`${backend}/api/signup/resend`, params);
    return res.data;
}

export async function cancel_signup_order(email: string, username: string) {
    const res = await axios.post(`${backend}/api/signup/cancel`, { email, username });
    return res.data;
}

export const update_ipfs_metadata = async (
    cid: string,
    ai_metadata: any,
    transcription: string,
    username: string,
    language?: string,
    srt_content?: string,
    transcription_en?: string,
    srt_content_en?: string
): Promise<any> => {
    try {
        const response = await axios.post(`${backend}/api/ipfs/metadata/update`, {
            cid,
            ai_metadata,
            transcription,
            username,
            language,
            srt_content,
            transcription_en,
            srt_content_en
        }, { headers: await getBackendHeaders() });
        return response.data;
    } catch (error) {
        console.warn("Metadata update was not completed.");
        return null;
    }
}

export const download_video = async (cid: string, username: string): Promise<any> => {
    try {
        const response = await authenticatedApi.post(`${aiBackend}/api/v1/download_video`, {
            cid,
            username,
});
        return response.data;
    } catch (error) {
        console.error("Error starting download task", error);
        return null;
    }
}

export const get_ipfs_metadata = async (cid: string): Promise<any> => {
    try {
        const response = await axios.get(`${backend}/api/ipfs/metadata/${cid}`);
        return response.data;
    } catch (error) {
        return null;
    }
}

export const get_leaderboard = async (week_start?: string): Promise<any> => {
    try {
        const response = await axios.post(`${backend}/api/leaderboard`, { week_start });
        return response.data || { entries: [] };
    } catch (error) {
        console.error('[db-service] get_leaderboard error', error);
        return { entries: [] };
    }
}

export const get_daily_leaderboard = async (date?: string): Promise<any> => {
    try {
        const response = await axios.post(`${backend}/api/leaderboard/daily`, { date });
        return response.data || { videos: [] };
    } catch (error) {
        console.error('[db-service] get_daily_leaderboard error', error);
        return { videos: [] };
    }
}

export const get_blacklist = async (user: string): Promise<any[]> => {
    try {
        const response = await authenticatedApi.get(`${backend}/api/blacklist`, { params: { user } });
        return response.data || [];
    } catch (error: any) {
        if (error?.response?.status !== 403) {
            console.error('[db-service] get_blacklist error', error);
        }
        return [];
    }
}

export const add_to_blacklist = async (user: string, username: string, reason: string): Promise<boolean> => {
    try {
        const response = await authenticatedApi.post(`${backend}/api/blacklist/add`, { user, username, reason });
        return !!response.data?.success;
    } catch (error) {
        console.error('[db-service] add_to_blacklist error', error);
        return false;
    }
}

export const remove_from_blacklist = async (user: string, username: string): Promise<boolean> => {
    try {
        const response = await authenticatedApi.post(`${backend}/api/blacklist/remove`, { user, username });
        return !!response.data?.success;
    } catch (error) {
        console.error('[db-service] remove_from_blacklist error', error);
        return false;
    }
}

export const get_site_settings = async (): Promise<any> => {
    try {
        const response = await axios.get(`${backend}/api/site_settings`);
        return response.data || {};
    } catch (error) {
        console.error('[db-service] get_site_settings error', error);
        return {};
    }
}

export const update_site_settings = async (user: string, settings: any): Promise<boolean> => {
    try {
        const response = await authenticatedApi.post(`${backend}/api/site_settings`, { user, settings });
        return !!response.data?.success;
    } catch (error) {
        console.error('[db-service] update_site_settings error', error);
        return false;
    }
}

export const get_muted_users = async (username: string): Promise<string[]> => {
    try {
        const response = await authenticatedApi.post(`${backend}/api/user/mute/list`, { username });
        return response.data || [];
    } catch (error) {
        console.error('[db-service] get_muted_users error', error);
        return [];
    }
}

export const mute_user = async (username: string, muted_user: string): Promise<boolean> => {
    try {
        const response = await authenticatedApi.post(`${backend}/api/user/mute/add`, { username, muted_user });
        return !!response.data?.success;
    } catch (error) {
        console.error('[db-service] mute_user error', error);
        return false;
    }
}

export const unmute_user = async (username: string, muted_user: string): Promise<boolean> => {
    try {
        const response = await authenticatedApi.post(`${backend}/api/user/mute/remove`, { username, muted_user });
        return !!response.data?.success;
    } catch (error) {
        console.error('[db-service] unmute_user error', error);
        return false;
    }
} 

export const promote_user_to_plus = async (user: string, target_username: string, quota: number): Promise<boolean> => {
    try {
        const response = await axios.post(`${backend}/api/admin/promote_plus`, { user, target_username, quota }, { headers: await getBackendHeaders() });
        return !!response.data?.success;
    } catch (error) {
        console.warn('[db-service] Promotion was not completed.');
        return false;
    }
}

export const demote_user_from_plus = async (user: string, target_username: string): Promise<boolean> => {
    try {
        const response = await axios.post(`${backend}/api/admin/demote_plus`, { user, target_username }, { headers: await getBackendHeaders() });
        return !!response.data?.success;
    } catch (error) {
        console.warn('[db-service] Demotion was not completed.');
        return false;
    }
}
export async function get_ai_download_url(filename: string): Promise<string> {
    const response = await authenticatedApi.post(`${backend}/api/ai/download-ticket`, { filename });
    return backend.replace(/\/$/, '') + response.data.path;
}

export async function get_feed_page(username?: string, cursor: number | null = null, limit = 20): Promise<{ posts: any[]; nextCursor: number | null }> {
    const response = await (username ? authenticatedApi : axios).post(`${backend}/api/custom_post_queue`,
        { username: username || null, limit, paginate: true, cursor });
    if (!Array.isArray(response.data?.posts)) throw new Error('Unable to load videos.');
    return { posts: parse(response.data.posts).filter(post => post.author && post.permlink), nextCursor: response.data.nextCursor ?? null };
}
